import styled, { css, keyframes } from 'styled-components';
import { mainTheme } from '../themes/mainTheme';

// The exit outlives `visible`, so Tooltip needs the same number in JS to know
// when the tooltip is finally gone. One const drives both.
export const tooltipMotionMs = parseInt(mainTheme.motion.duration.fast, 10);

// `translate` rather than `transform` — popper owns the root's transform.
const reveal = keyframes`
  from {
    opacity: 0;
    translate: var(--tooltip-enter-x) var(--tooltip-enter-y);
  }
  to {
    opacity: 1;
    translate: 0 0;
  }
`;

// Reveal run backwards. A separate name rather than `direction: reverse` so that
// swapping it restarts the animation — a direction change on a finished one does not.
const dismiss = keyframes`
  from {
    opacity: 1;
    translate: 0 0;
  }
  to {
    opacity: 0;
    translate: var(--tooltip-enter-x) var(--tooltip-enter-y);
  }
`;

// Figma: 01. Component Library / Tooltip (node 497:2581).
// One look only — light chip, dark label, no border, no shadow. The label as
// written unless the caller asks for uppercase.
// 10px across the base, 6px from base to point, on every side.
export const tooltipTipWidth = 10;
export const tooltipTipDepth = 6;
// The base sits on the chip edge and this much of the shape carries on inside
// it, so tip and body overlap instead of meeting on a hairline that subpixel
// offsets can open up.
const tooltipTipOverlap = 2;
const tooltipTipSpan = tooltipTipDepth + tooltipTipOverlap;
// How far the chip sits from its trigger: the tip plus 2px of air, so the point
// stops just short of the trigger instead of landing on it.
export const tooltipTipOffset = tooltipTipDepth + 2;

export const TooltipRoot = styled.div<{ $exiting: boolean; $rich?: boolean; $card?: boolean; $uppercase?: boolean }>`
  display: flex;
  align-items: center;
  min-height: 36px;
  /* Past 240px a label wraps, left aligned, with at least 4px above and below it. */
  max-width: 240px;
  padding: 4px 12px;
  border-radius: 4px;
  background: ${props => props.theme.fill.secondary};
  color: ${props => props.theme.text.inverted};
  font-family: ${props => props.theme.font};
  font-size: 14px;
  font-weight: 510;
  line-height: 1.4;
  text-align: left;
  z-index: 9999;

  /* Uppercase needs tracking to stay readable; sentence case does not, so the
     two travel together. */
  ${({ $uppercase }) => $uppercase && css`
    letter-spacing: 0.28px;
    text-transform: uppercase;
  `}

  /* Slides 4px out of the trigger it belongs to. */
  --tooltip-enter-x: 0;
  --tooltip-enter-y: 0;
  animation: ${props => props.$exiting ? dismiss : reveal}
    ${tooltipMotionMs}ms
    ${props => props.theme.motion.easing.default} forwards;

  // An exiting tooltip is still on screen; it must not swallow the pointer.
  ${({ $exiting }) => $exiting && css`
    pointer-events: none;
  `}

  /* A chip carries one label. Rows are a list, and a list needs
     columns: labels down one, values down the other, so the eye finds a row's
     value without reading the row. Same chip colours — only the layout and the
     type change. */
  ${({ $rich }) => $rich && css`
    display: grid;
    grid-template-columns: max-content 1fr;
    column-gap: 16px;
    row-gap: 4px;
    align-items: baseline;
    min-height: 0;
    padding: 12px;
    font-weight: 400;
  `}

  /* A hover card: the dropdown menu's lifted surface and shadow, no edge, no tip. */
  ${({ $card, theme }) => $card && css`
    display: block;
    min-height: 0;
    max-width: none;
    padding: 0;
    background: ${theme.form.dropdown.menu.default};
    box-shadow: ${theme.form.dropdown.menu.shadow};
    color: ${theme.text.secondary};
    font-weight: 400;

    .tooltip-arrow {
      display: none;
    }
  `}

  &[data-popper-placement*='top'] {
    --tooltip-enter-y: 4px;
  }

  &[data-popper-placement*='bottom'] {
    --tooltip-enter-y: -4px;
  }

  &[data-popper-placement*='left'] {
    --tooltip-enter-x: 4px;
  }

  &[data-popper-placement*='right'] {
    --tooltip-enter-x: -4px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  &[data-popper-interactive='false'] {
    pointer-events: none;
  }

  /* One square box for every placement. Popper centres the arrow from the box
     it measures, and it measures before the placement attribute lands — a box
     that changed shape per side came out 4px off on left and right. The tip
     itself is drawn by ::before, which is sized per side. */
  .tooltip-arrow {
    position: absolute;
    /* A stray 1.5px transparent border reaches this div from outside the
       component. With border-box that shrinks the padding box to 7px, and the
       tip below anchors to that box — 2px adrift and off-centre. */
    border: 0;
    width: ${tooltipTipWidth}px;
    height: ${tooltipTipWidth}px;
    pointer-events: none;
  }

  .tooltip-arrow::before {
    content: '';
    position: absolute;
    background: ${props => props.theme.fill.secondary};
  }

  /* Box edge ${tooltipTipDepth}px past the chip edge; the tip fills that and
     laps ${tooltipTipOverlap}px back inside. Each clip-path is the same
     triangle — point, the two base corners on the chip edge, then the two
     corners of the overlap hidden in the chip. */
  &[data-popper-placement*='top'] .tooltip-arrow {
    bottom: -${tooltipTipDepth}px;
  }

  &[data-popper-placement*='top'] .tooltip-arrow::before {
    left: 0;
    bottom: 0;
    width: ${tooltipTipWidth}px;
    height: ${tooltipTipSpan}px;
    clip-path: polygon(
      50% 100%,
      100% ${tooltipTipOverlap}px,
      100% 0,
      0 0,
      0 ${tooltipTipOverlap}px
    );
  }

  &[data-popper-placement*='bottom'] .tooltip-arrow {
    top: -${tooltipTipDepth}px;
  }

  &[data-popper-placement*='bottom'] .tooltip-arrow::before {
    left: 0;
    top: 0;
    width: ${tooltipTipWidth}px;
    height: ${tooltipTipSpan}px;
    clip-path: polygon(
      50% 0,
      100% ${tooltipTipDepth}px,
      100% 100%,
      0 100%,
      0 ${tooltipTipDepth}px
    );
  }

  &[data-popper-placement*='left'] .tooltip-arrow {
    right: -${tooltipTipDepth}px;
  }

  &[data-popper-placement*='left'] .tooltip-arrow::before {
    top: 0;
    right: 0;
    width: ${tooltipTipSpan}px;
    height: ${tooltipTipWidth}px;
    clip-path: polygon(
      100% 50%,
      ${tooltipTipOverlap}px 100%,
      0 100%,
      0 0,
      ${tooltipTipOverlap}px 0
    );
  }

  &[data-popper-placement*='right'] .tooltip-arrow {
    left: -${tooltipTipDepth}px;
  }

  &[data-popper-placement*='right'] .tooltip-arrow::before {
    top: 0;
    left: 0;
    width: ${tooltipTipSpan}px;
    height: ${tooltipTipWidth}px;
    clip-path: polygon(
      0 50%,
      ${tooltipTipDepth}px 100%,
      100% 100%,
      100% 0,
      ${tooltipTipDepth}px 0
    );
  }
`;

/* Spans both columns. Dimmed and set off by a wider gap than the rows keep
   between themselves — enough to read as a heading without a rule under it. */
export const TooltipTitle = styled.div`
  grid-column: 1 / -1;
  margin-bottom: 8px;
  color: ${props => props.theme.text.invertedSecondary};
`;

export const TooltipRowLabel = styled.div`
  color: ${props => props.theme.text.invertedSecondary};
`;

// Values carry the weight, and a link keeps its underline — the two things that
// tell a value from its label, and a link from plain text, without a colour of
// their own.
export const TooltipRowValue = styled.div`
  color: ${props => props.theme.text.inverted};
  font-weight: 510;

  a {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  a:hover,
  a:focus-visible {
    text-decoration-thickness: 2px;
  }
`;
